import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { readMore } from "./constant";
import styles from "./index.module.css";

import olisaPortrait from "../../images/about-redesign/olisa-okeke.png";
import bernadinePortrait from "../../images/about-redesign/bernadine-okeke.png";
import peterPortrait from "../../images/about-redesign/peter-eze.jpg";
import kaluPortrait from "../../images/about-redesign/kalu-nnachi.jpg";
import matthewPortrait from "../../images/about-redesign/matthew-eze.jpg";
import ijeomaPortrait from "../../images/about-redesign/ijeoma-obiejesi.jpg";
import darlingtonPortrait from "../../images/about-redesign/darlington-chima.jpg";
import agnesPortrait from "../../board-images/Agnes Odeh.jpg";
import comfortPortrait from "../../images/about-redesign/comfort-onuoha.jpeg";
import samuelPortrait from "../../images/about-redesign/samuel-agu.jpeg";
import chidoziePortrait from "../../board-images/chidozieAgbapu.png";
import ikePortrait from "../../board-images/IkeOnyechi.png";
import tonyPortrait from "../../board-images/Ifeacho.png";
import johnboscoPortrait from "../../images/about-redesign/johnbosco-okolocha.jpg";

export const directors = [
	{ ...readMore.olisa, image: olisaPortrait },
	{ ...readMore.bernadine, image: bernadinePortrait },
	{ ...readMore.Peter, image: peterPortrait },
	{ ...readMore.chidozie, image: chidoziePortrait },
	{ ...readMore.onyechi, image: ikePortrait },
	{ ...readMore.tony, image: tonyPortrait },
];

const compact = (person) => person.profile?.[0]?.trim().replace(/\s+/g, " ") || "";

const ijeoma = {
	name: "Ijeoma Obiejesi",
	role: "Financial Controller",
	image: ijeomaPortrait,
	profile: [
		"Ijeoma Obiejesi is a finance professional with six years of working experience. She holds a Bachelor of Science degree in Accounting from Chukwuemeka Odumegwu Ojukwu University, Igbariam, Anambra State, and is a Certified Member of the Microfinance Chartered Institute of Bankers (MICB).",
		"She has experience in financial management, reporting, and planning, with a focus on accuracy, accountability, and operational efficiency.",
	],
};

const management = [
	{ ...readMore.matthew, image: matthewPortrait },
	{ ...readMore.uche, image: darlingtonPortrait },
	ijeoma,
	{ ...readMore.agnes, image: agnesPortrait },
	{ name: "Comfort Chioma Onuoha", role: "Head of Digital Assets/E-Business", image: comfortPortrait, profile: [
		"With over a decade of experience in digital banking and financial technology, Comfort Chioma Onuoha is a results-driven digital banking executive with deep expertise in channel management, payments, and enterprise platform development. She began her banking career at Diamond Bank Plc and joined Zikora Microfinance Bank Limited in 2022, where she has led the development of the bank’s enterprise digital platform and the end-to-end migration onto the NIBSS Payment Scheme (NPS). She oversees all digital channels including Mobile Banking, Internet Banking, USSD, POS/Agency Banking, and ATM operations. She holds an HND in Computer Science and is a member of both the Chartered Institute of Bankers of Nigeria (CIBN) and the Microfinance Chartered Institute of Bankers Nigeria (MCIB).",
	] },
	{ name: "Samuel Chukwuebuka Agu", role: "Acting Head, Information Technology", image: samuelPortrait, profile: [
		"Samuel Agu holds a Degree in Computer Engineering from Enugu State University of Science and Technology and brings over 10 years of hands-on experience as an IT and Cybersecurity Professional specializing in network security, infrastructure management, hardware maintenance, and ICT operations. He has a demonstrated history of designing, deploying, and securing enterprise-grade network systems, backed by practical expertise in CCNA-level networking, LAN architecture, vulnerability assessment, and system hardening. His core strengths include detecting and addressing security vulnerabilities, implementing IT security protocols, and ensuring alignment with industry compliance standards. Samuel is experienced in spearheading cross-functional IT initiatives, coaching technical staff, and resolving intricate network and hardware challenges under pressure. He is dedicated to protecting organizational infrastructure while enabling business growth through secure, efficient, and scalable technology strategies.",
	] },
	{ ...readMore.johnbosco, image: johnboscoPortrait },
];

const BiographyDialog = ({ person, onClose }) => {
	const closeRef = useRef(null);
	const reducedMotion = useReducedMotion();

	useEffect(() => {
		const previousOverflow = document.body.style.overflow;
		const previousFocus = document.activeElement;
		document.body.style.overflow = "hidden";
		closeRef.current?.focus();
		const handleKey = (event) => {
			if (event.key === "Escape") onClose();
		};
		window.addEventListener("keydown", handleKey);
		return () => {
			document.body.style.overflow = previousOverflow;
			window.removeEventListener("keydown", handleKey);
			previousFocus?.focus?.();
		};
	}, [onClose]);

	return (
		<motion.div className={styles.bioBackdrop} onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}
			initial={reducedMotion ? false : { opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
			transition={{ duration: reducedMotion ? 0 : 0.3, ease: "easeOut" }}>
			<motion.div className={styles.bioDialog} role="dialog" aria-modal="true" aria-labelledby="biography-name"
				initial={reducedMotion ? false : { opacity: 0, y: 18 }}
				animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
				transition={{ duration: reducedMotion ? 0 : 0.38, ease: [0.22, 1, 0.36, 1] }}>
				<button ref={closeRef} className={styles.bioClose} type="button" onClick={onClose} aria-label="Close biography">×</button>
				<div className={styles.bioHeader}>
					<img src={person.image} alt="" />
					<div><span>{person.role}</span><h3 id="biography-name">{person.name}</h3></div>
				</div>
				<div className={styles.bioBody}>{person.profile.map((paragraph, index) => <p key={index}>{paragraph.trim()}</p>)}</div>
			</motion.div>
		</motion.div>
	);
};

const PeopleCarousel = ({ title, people, onSelect }) => {
	const [start, setStart] = useState(0);
	const [visible, setVisible] = useState(3);
	const [stepWidth, setStepWidth] = useState(0);
	const gridRef = useRef(null);
	const reducedMotion = useReducedMotion();

	useEffect(() => {
		const update = () => setVisible(window.innerWidth < 760 ? 1 : window.innerWidth < 1024 ? 2 : 3);
		update();
		window.addEventListener("resize", update);
		return () => window.removeEventListener("resize", update);
	}, []);

	useEffect(() => {
		const grid = gridRef.current;
		if (!grid) return undefined;
		const measure = () => {
			const card = grid.querySelector("article");
			if (card) setStepWidth(card.getBoundingClientRect().width + parseFloat(getComputedStyle(grid).gap || 0));
		};
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(grid);
		return () => observer.disconnect();
	}, [visible]);

	const maximum = Math.max(0, people.length - visible);
	const current = Math.min(start, maximum);
	const move = (step) => setStart(Math.max(0, Math.min(maximum, current + step)));

	return (
		<div className={styles.peopleGroup}>
			<div className={styles.peopleHeading}>
				<h3>{title}</h3>
				<div className={styles.peopleControls}>
					<button type="button" aria-label={`Previous ${title}`} disabled={current === 0} onClick={() => move(-1)}>‹</button>
					<button type="button" aria-label={`Next ${title}`} disabled={current === maximum} onClick={() => move(1)}>›</button>
				</div>
			</div>
			<div className={styles.peopleViewport}>
			<motion.div ref={gridRef} className={styles.peopleGrid} animate={{ x: -current * stepWidth }}
				transition={reducedMotion ? { duration: 0 } : { type: "spring", stiffness: 115, damping: 25, mass: 0.9 }}>
				{people.map((person, index) => (
					<article className={styles.personCard} key={person.name}>
						<img src={person.image} alt={person.name} loading="lazy" decoding="async" />
						<span className={styles.personRole}>{person.role}</span>
						<h4>{person.name}</h4>
						<p>{compact(person).slice(0, 122)}{compact(person).length > 122 ? "…" : ""} <button type="button" tabIndex={index >= current && index < current + visible ? 0 : -1} onClick={() => onSelect(person)}>Read more</button></p>
					</article>
				))}
			</motion.div>
			</div>
		</div>
	);
};

const Leadership = () => {
	const [selected, setSelected] = useState(null);

	return (
		<section className={styles.leadership}>
			<div className={styles.sectionShell}>
				<div className={styles.sectionHeading}>
					<div className={styles.label}><span aria-hidden="true" />Leadership</div>
					<h2>Meet the people leading Zikora</h2>
					<p>A team rooted in the communities we serve, building a bank we’d want for our own families.</p>
				</div>
				<PeopleCarousel title="Board of Directors" people={directors} onSelect={setSelected} />
				<div className={styles.leaderFeature}>
					<img className={styles.leaderImage} src={kaluPortrait} alt="Kalu Keinley Nnachi" loading="lazy" decoding="async" />
					<div className={styles.leaderCopy}>
						<div className={styles.label}>Chief Executive Officer</div>
						<h3>Kalu Keinley Nnachi</h3>
						<p className={styles.leaderTitle}>CEO, Zikora Bank</p>
						<p>Kalu is an experienced Commercial and Certified Microfinance Banker, a Marketing Executive, a Financial &amp; Investment Analysis Enthusiast and a Business Leader. He has over a decade of Banking experience which cut across Banking Operations, Retail Banking, Customer Service and Marketing.</p>
						<button className={styles.readMore} type="button" onClick={() => setSelected({ ...readMore.kalu, image: kaluPortrait })}>Read more</button>
					</div>
				</div>
				<PeopleCarousel title="Management Team" people={management} onSelect={setSelected} />
			</div>
			<AnimatePresence>{selected && <BiographyDialog person={selected} onClose={() => setSelected(null)} />}</AnimatePresence>
		</section>
	);
};

export default Leadership;
